import React, { useEffect, useRef, useState } from "react";
import PageCTA from '../components/sections/pageCTA.jsx';

// ─── ICON ──────────────────────────────────────────────────────────────────────
const Icon = ({ d, size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"
    strokeLinejoin="round" className={className}>
    <path d={d} />
  </svg>
);

const ICONS = {
  chaos: "M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0 0v-8m0 0L8 9m4 5l4-5",
  heka: "M13 10V3L4 14h7v7l9-11h-7z",
  order: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  cube: "M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z",
  brain: "M12 2a3 3 0 00-3 3v1a3 3 0 00-3 3v3a3 3 0 003 3h6a3 3 0 003-3V9a3 3 0 00-3-3V5a3 3 0 00-3-3z M9 18a3 3 0 003 3 3 3 0 003-3",
  network: "M12 2L2 7l10 5 10-5-10-5z M2 17l10 5 10-5 M2 12l10 5 10-5",
  workflow: "M4 4h4v4H4zm8 0h4v4h-4zm8 0h4v4h-4zM4 12h4v4H4zm8 0h4v4h-4zm8 0h4v4h-4z",
  rocket: "M4.5 16.5c-1.5 1.36-2 3.5-2 5.5l5.5-2s2.14-.5 3.5-2L21 8.5l-6-6L5.5 12c-1.5 1.36-1 3.5-1 4.5z",
  server: "M3 4h18v4H3zm0 6h18v4H3zm0 6h18v4H3z M7 8h.01M7 14h.01M7 20h.01",
  pipeline: "M5 8h14M5 16h14 M3 4h18v16H3z",
  agent: "M12 2L2 7l10 5 10-5-10-5z M2 17l10 5 10-5",
  whatsapp: "M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z",
  arrow: "M5 12h14M12 5l7 7-7 7",
  sparkles: "M12 2l1.09 3.26L16 6.35l-3.26 1.09L12 11l-1.09-3.26L8 6.35l3.26-1.09L12 2z M5 12l.54 1.63L7 14.17l-1.63.54L5 16.34l-.54-1.63L3 14.17l1.63-.54L5 12z M19 12l.54 1.63L21 14.17l-1.63.54L19 16.34l-.54-1.63L17 14.17l1.63-.54L19 12z",
};

// ─── STYLES ────────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  .pp-title {
    background: linear-gradient(110deg, #fbbf24 35%, #a78bfa 50%, #fbbf24 65%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: pp-shimmer 8s linear infinite;
  }
  @keyframes pp-shimmer {
    0%   { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }

  .pp-dot-grid {
    background-image: radial-gradient(circle, rgba(251,191,36,0.12) 1px, transparent 1px);
    background-size: 28px 28px;
  }

  .pp-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(251,191,36,0.3), transparent);
  }

  @keyframes pp-fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .pp-fade   { animation: pp-fadeUp 0.7s ease forwards; }
  .pp-fade-1 { animation-delay: 0.05s; opacity: 0; }
  .pp-fade-2 { animation-delay: 0.15s; opacity: 0; }
  .pp-fade-3 { animation-delay: 0.25s; opacity: 0; }
  .pp-fade-4 { animation-delay: 0.35s; opacity: 0; }
  .pp-fade-5 { animation-delay: 0.45s; opacity: 0; }

  /* ── Transformation Cycle ── */
  .pp-cycle-node {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  .pp-cycle-node:hover {
    transform: translateY(-3px);
  }

  @keyframes pp-flow {
    0%   { stroke-dashoffset: 24; opacity: 0.3; }
    50%  { opacity: 1; }
    100% { stroke-dashoffset: 0; opacity: 0.3; }
  }
  .pp-arrow-anim       { stroke-dasharray: 8 4; animation: pp-flow 1.8s ease-in-out infinite; }
  .pp-arrow-anim-delay { stroke-dasharray: 8 4; animation: pp-flow 1.8s ease-in-out 0.9s infinite; }

  @keyframes pp-heka-ring {
    0%   { transform: scale(1);    opacity: 0.35; }
    70%  { transform: scale(1.55); opacity: 0; }
    100% { transform: scale(1.55); opacity: 0; }
  }
  .pp-heka-ring { animation: pp-heka-ring 2.4s ease-out infinite; }

  /* ── Platform Cards ── */
  .pp-platform-card {
    transition: transform 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s ease, border-color 0.3s ease;
  }
  .pp-platform-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 40px rgba(0,0,0,0.3);
  }

  /* ── Core Engine Accent - Rotating cube ── */
  @keyframes pp-cube-rotate {
    from { transform: rotateX(20deg) rotateY(0deg); }
    to   { transform: rotateX(20deg) rotateY(360deg); }
  }
  .pp-cube-3d {
    animation: pp-cube-rotate 8s linear infinite;
    transform-style: preserve-3d;
  }

  /* ── AI Pipelines Accent - Flowing nodes ── */
  @keyframes pp-pipeline-flow {
    0%   { stroke-dashoffset: 200; }
    100% { stroke-dashoffset: 0; }
  }
  .pp-pipeline-flow {
    animation: pp-pipeline-flow 3s linear infinite;
  }

  @keyframes pp-pipeline-pulse {
    0%, 100% { r: 4; opacity: 0.5; }
    50%      { r: 6; opacity: 1; }
  }
  .pp-pipeline-pulse {
    animation: pp-pipeline-pulse 2s ease-in-out infinite;
  }

  @keyframes pp-pipeline-ring {
    0%   { r: 5; opacity: 0.4; }
    50%  { r: 8; opacity: 0.1; }
    100% { r: 5; opacity: 0.4; }
  }
  .pp-pipeline-ring {
    animation: pp-pipeline-ring 2s ease-in-out infinite;
  }

  /* ── Agent Swarm Accent - Orbiting dots ── */
  @keyframes pp-swarm-orbit {
    from { transform: rotate(0deg) translateX(36px) rotate(0deg); }
    to   { transform: rotate(360deg) translateX(36px) rotate(-360deg); }
  }
  .pp-swarm-dot-1 { animation: pp-swarm-orbit 4s linear infinite; }
  .pp-swarm-dot-2 { animation: pp-swarm-orbit 4s linear 1.3s infinite; }
  .pp-swarm-dot-3 { animation: pp-swarm-orbit 4s linear 2.6s infinite; }

  /* ── Product Showcase ── */
  .pp-product-card {
    transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .pp-product-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 24px 60px rgba(0,0,0,0.35);
  }

  @keyframes pp-feature-pop {
    0%   { transform: scale(0.9); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }
  .pp-feature-item {
    animation: pp-feature-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
  }
  .pp-feature-item:nth-child(1) { animation-delay: 0.1s; }
  .pp-feature-item:nth-child(2) { animation-delay: 0.2s; }
  .pp-feature-item:nth-child(3) { animation-delay: 0.3s; }
  .pp-feature-item:nth-child(4) { animation-delay: 0.4s; }

  /* ── Stack Architecture ── */
  @keyframes pp-layer-slide {
    0%, 100% { transform: translateX(0); }
    50%       { transform: translateX(8px); }
  }
  .pp-stack-layer {
    animation: pp-layer-slide 3s ease-in-out infinite;
  }
  .pp-stack-layer:nth-child(1) { animation-delay: 0s; }
  .pp-stack-layer:nth-child(2) { animation-delay: 0.3s; }
  .pp-stack-layer:nth-child(3) { animation-delay: 0.6s; }
  .pp-stack-layer:nth-child(4) { animation-delay: 0.9s; }

  /* ── CTA Buttons ── */
  .pp-btn-primary {
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
  }
  .pp-btn-primary::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }
  .pp-btn-primary:hover::after {
    transform: translateX(100%);
  }
  .pp-btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(217,119,6,0.35);
    background-color: #d97706;
  }

  .pp-btn-secondary {
    transition: all 0.3s ease;
  }
  .pp-btn-secondary:hover {
    transform: translateY(-2px);
    background: rgba(109,40,217,0.1);
    border-color: rgba(109,40,217,0.6);
    color: #c4b5fd;
  }

  /* ── Stats Counter ── */
  @keyframes pp-count-up {
    from { transform: translateY(10px); opacity: 0; }
    to   { transform: translateY(0); opacity: 1; }
  }
  .pp-stat-value {
    animation: pp-count-up 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }
`;

// ─── DATA ─────────────────────────────────────────────────────────────────────
const PRODUCTS_DATA = [
  {
    name: "HEKA Atlas",
    tagline: "Where roles meet the right people",
    desc: "One platform connecting role requirements to the right candidates — end to end.",
    color: "#f59e0b",
    gradient: "from-amber-500 to-yellow-600",
    icon: ICONS.workflow,
    accent: "cube",
    features: ["Role Specification Engine", "Candidate Profile Matching", "ATS Optimization", "Hiring Workflow Automation", "End-to-End Analytics"],
  },
  {
    name: "HEKA Codex",
    tagline: "Every role, precisely defined",
    desc: "One workspace to create, parse, and optimize role specifications — built for hiring teams who need clarity, not complexity.",
    color: "#06b6d4",
    gradient: "from-cyan-500 to-blue-600",
    icon: ICONS.sparkles,
    accent: "swarm",
    features: ["AI-Powered Job Description Generator", "Security Clearances", "Structured Approval Workflows", "Hiring Workflow Automation"],
  },
  {
    name: "HEKA Odyssey",
    tagline: "Your career, precisely positioned.",
    desc: "Analyze, optimize, and position your career — from first application to the right offer.",
    color: "#8b5cf6",
    gradient: "from-violet-500 to-purple-600",
    icon: ICONS.brain,
    accent: "pipeline",
    features: ["Resume Screening & Scoring", "Skill Gap Analysis", "Candidate-Role Match Engine", "Cover Letter Optimization"],
  },
];



const STACK_LAYERS = [
  { name: "Interface Layer", desc: "User-facing apps and API endpoints", color: "#06b6d4" },
  { name: "Intelligence Layer", desc: "AI models, agents, and reasoning engines", color: "#8b5cf6" },
  { name: "Data Layer", desc: "Knowledge graphs, vector stores, caching", color: "#ec4899" },
  { name: "Infrastructure", desc: "Compute, storage, monitoring, security", color: "#f59e0b" },
];

const PLATFORM_STATS = [
  { value: "99.9%", label: "Platform Uptime", color: "#06b6d4" },
  { value: "12+", label: "AI Models Deployed", color: "#8b5cf6" },
  { value: "<100ms", label: "Avg Response Time", color: "#ec4899" },
  { value: "1M+", label: "Decisions/Month", color: "#f59e0b" },
];

// ─── TRANSFORMATION CYCLE ──────────────────────────────────────────────────────
const TransformationCycle = () => (
  <section className="pp-fade pp-fade-2">
    <div className="text-center mb-10">
      <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
        The Foundry Process
      </p>
      <h2 className="text-2xl font-semibold text-slate-100 tracking-tight">
        From Raw Intelligence to Engineered Products
      </h2>
    </div>

    {/* Desktop Layout */}
    <div className="hidden md:flex items-center justify-center gap-0 max-w-5xl mx-auto">
      {/* CHAOS */}
      <div className="pp-cycle-node flex flex-col items-center gap-4 w-56">
        <div className="w-24 h-24 rounded-2xl border border-red-800/40 bg-red-900/20 flex items-center justify-center"
          style={{ boxShadow: "0 0 28px rgba(239,68,68,0.12)" }}>
          <Icon d={ICONS.chaos} size={38} className="text-red-400" />
        </div>
        <div className="text-center">
          <p className="text-red-400 font-bold text-sm tracking-widest uppercase mb-1.5">RAW</p>
          <p className="text-slate-500 text-sm leading-relaxed">Unformed ideas.<br />Potential without structure.</p>
        </div>
      </div>

      {/* Arrow 1 */}
      <div className="flex flex-col items-center gap-2 w-28 flex-shrink-0 pb-14">
        <svg width="112" height="24" viewBox="0 0 112 24" fill="none">
          <line x1="0" y1="12" x2="96" y2="12" stroke="#ef4444" strokeWidth="2" className="pp-arrow-anim" />
          <polyline points="86,5 100,12 86,19" stroke="#ef4444" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-slate-600 text-xs font-semibold tracking-widest uppercase">FOUNDRY</span>
      </div>

      {/* HEKA */}
      <div className="pp-cycle-node flex flex-col items-center gap-4 relative">
        <div className="absolute w-36 h-36 rounded-full border border-violet-500/15 pp-heka-ring" />
        <div className="w-36 h-36 rounded-2xl border border-violet-600/50 flex items-center justify-center relative z-10"
          style={{
            background: "linear-gradient(135deg, rgba(109,40,217,0.28), rgba(139,92,246,0.16))",
            boxShadow: "0 0 48px rgba(139,92,246,0.22), inset 0 1px 0 rgba(167,139,250,0.15)",
          }}>
          <Icon d={ICONS.heka} size={52} className="text-violet-400" />
        </div>
        <div className="text-center mt-1">
          <p className="text-violet-400 font-bold text-base tracking-widest uppercase mb-1.5">FOUNDRY</p>
          <p className="text-slate-500 text-sm leading-relaxed max-w-[11rem]">Engineering under pressure.<br />Shape emerges from heat.</p>

        </div>
      </div>

      {/* Arrow 2 */}
      <div className="flex flex-col items-center gap-2 w-28 flex-shrink-0 pb-14">
        <svg width="112" height="24" viewBox="0 0 112 24" fill="none">
          <line x1="0" y1="12" x2="96" y2="12" stroke="#22d3ee" strokeWidth="2" className="pp-arrow-anim-delay" />
          <polyline points="86,5 100,12 86,19" stroke="#22d3ee" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-slate-600 text-xs font-semibold tracking-widest uppercase">DEPLOY</span>

      </div>

      {/* ORDER */}
      <div className="pp-cycle-node flex flex-col items-center gap-4 w-56">
        <div className="w-24 h-24 rounded-2xl border border-cyan-800/40 bg-cyan-900/20 flex items-center justify-center"
          style={{ boxShadow: "0 0 28px rgba(34,211,238,0.12)" }}>
          <Icon d={ICONS.order} size={38} className="text-cyan-400" />
        </div>
        <div className="text-center">
          <p className="text-cyan-400 font-bold text-sm tracking-widest uppercase mb-1.5">ENGINEERED</p>
          <p className="text-slate-500 text-sm leading-relaxed">Hardened product.<br />Built to operate at scale.</p>
        </div>
      </div>
    </div>

    {/* Mobile Layout */}
    <div className="md:hidden flex flex-col items-center gap-0 max-w-sm mx-auto">
      <div className="pp-cycle-node flex items-center gap-5 w-full rounded-xl border border-red-800/30 bg-slate-900 px-6 py-5">
        <div className="w-16 h-16 rounded-xl border border-red-800/40 bg-red-900/20 flex items-center justify-center flex-shrink-0">
          <Icon d={ICONS.chaos} size={28} className="text-red-400" />
        </div>
        <div>
          <p className="text-red-400 font-bold text-sm tracking-widest uppercase mb-1">RAW</p>
          <p className="text-slate-500 text-sm leading-relaxed">Unformed ideas. Potential without structure.</p>
        </div>
      </div>

      <div className="flex flex-col items-center py-3">
        <svg width="24" height="44" viewBox="0 0 24 44" fill="none">
          <line x1="12" y1="0" x2="12" y2="34" stroke="#a78bfa" strokeWidth="2" strokeDasharray="6 3"
            style={{ animation: "pp-flow 1.8s ease-in-out infinite" }} />
          <polyline points="5,26 12,38 19,26" stroke="#a78bfa" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div className="pp-cycle-node flex items-center gap-5 w-full rounded-xl px-6 py-5"
        style={{ border: "1px solid rgba(139,92,246,0.4)", background: "linear-gradient(135deg, rgba(109,40,217,0.2), rgba(139,92,246,0.1))", boxShadow: "0 0 32px rgba(139,92,246,0.18)" }}>
        <div className="w-16 h-16 rounded-xl border border-violet-600/40 bg-violet-900/30 flex items-center justify-center flex-shrink-0">
          <Icon d={ICONS.heka} size={30} className="text-violet-400" />
        </div>
        <div>
          <p className="text-violet-400 font-bold text-sm tracking-widest uppercase mb-1">FOUNDRY</p>
          <p className="text-slate-400 text-sm leading-relaxed">Engineering under pressure. Shape emerges from heat.</p>
        </div>
      </div>

      <div className="flex flex-col items-center py-3">
        <svg width="24" height="44" viewBox="0 0 24 44" fill="none">
          <line x1="12" y1="0" x2="12" y2="34" stroke="#22d3ee" strokeWidth="2" strokeDasharray="6 3"
            style={{ animation: "pp-flow 1.8s ease-in-out 0.9s infinite" }} />
          <polyline points="5,26 12,38 19,26" stroke="#22d3ee" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div className="pp-cycle-node flex items-center gap-5 w-full rounded-xl border border-cyan-800/30 bg-slate-900 px-6 py-5">
        <div className="w-16 h-16 rounded-xl border border-cyan-800/40 bg-cyan-900/20 flex items-center justify-center flex-shrink-0">
          <Icon d={ICONS.order} size={28} className="text-cyan-400" />
        </div>
        <div>
          <p className="text-cyan-400 font-bold text-sm tracking-widest uppercase mb-1">ENGINEERED</p>
          <p className="text-slate-500 text-sm leading-relaxed">Hardened product. Built to operate at scale.</p>
        </div>
      </div>
    </div>
  </section>
);

// ─── PLATFORM ACCENTS ──────────────────────────────────────────────────────────
const CubeAccent = () => (
  <div className="relative h-24 flex items-center justify-center perspective-1000">
    <div className="pp-cube-3d w-20 h-20 relative">
      <div className="absolute inset-0 border-2 border-violet-400/40 rounded-lg"
        style={{ transform: "translateZ(-10px)" }} />
      <div className="absolute inset-0 border-2 border-violet-400/60 rounded-lg"
        style={{ transform: "translateZ(10px)" }} />
      <div className="absolute inset-0 bg-violet-400/10 rounded-lg" />
    </div>
  </div>
);

const PipelineAccent = () => (
  <div className="relative w-24 h-20 flex items-center justify-center">
    <svg width="96" height="80" viewBox="0 0 96 80" fill="none">
      {/* Pipeline path */}
      <path d="M 8,40 L 32,40 L 48,20 L 64,20 L 80,40 L 88,40"
        stroke="rgba(6,182,212,0.25)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      {/* Animated flow */}
      <path d="M 8,40 L 32,40 L 48,20 L 64,20 L 80,40 L 88,40"
        stroke="rgba(6,182,212,0.9)" strokeWidth="3" fill="none"
        strokeDasharray="15 10" className="pp-pipeline-flow" strokeLinecap="round" />
      {/* Pulsing nodes */}
      <circle cx="20" cy="40" r="3.5" fill="rgba(6,182,212,0.6)" className="pp-pipeline-pulse" />
      <circle cx="56" cy="20" r="3.5" fill="rgba(6,182,212,0.6)" className="pp-pipeline-pulse" style={{ animationDelay: '0.5s' }} />
      <circle cx="84" cy="40" r="3.5" fill="rgba(6,182,212,0.6)" className="pp-pipeline-pulse" style={{ animationDelay: '1s' }} />
      {/* Connection nodes */}
      <circle cx="20" cy="40" r="5" fill="none" stroke="rgba(6,182,212,0.4)" strokeWidth="1" className="pp-pipeline-ring" />
      <circle cx="56" cy="20" r="5" fill="none" stroke="rgba(6,182,212,0.4)" strokeWidth="1" className="pp-pipeline-ring" style={{ animationDelay: '0.5s' }} />
      <circle cx="84" cy="40" r="5" fill="none" stroke="rgba(6,182,212,0.4)" strokeWidth="1" className="pp-pipeline-ring" style={{ animationDelay: '1s' }} />
    </svg>
  </div>
);

const SwarmAccent = () => (
  <div className="relative h-24 flex items-center justify-center">
    <div className="w-4 h-4 rounded-full bg-pink-400/60 border border-pink-400" />
    <div className="absolute w-20 h-20 rounded-full border border-dashed border-pink-400/20" />
    {[1, 2, 3].map((i) => (
      <div key={i} className={`absolute pp-swarm-dot-${i}`} style={{ width: 72, height: 72 }}>
        <div className="w-2.5 h-2.5 rounded-full bg-pink-400 shadow-[0_0_10px_rgba(236,72,153,0.8)]" />
      </div>
    ))}
  </div>
);

// ─── PRODUCTS SECTION ─────────────────────────────────────────────────────────
const ProductsSection = () => (
  <section className="pp-fade pp-fade-3">
    <div className="text-center mb-10">
      <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
        Products
      </p>
      <h2 className="text-2xl font-semibold text-slate-100 tracking-tight mb-4">
        Built in the Foundry
      </h2>
      <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed text-sm">
        Every product forged from first principles — engineered to solve real problems, built to operate under pressure.
      </p>
    </div>

    {/* Bento Grid */}
    <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-6 md:h-[580px]">

      {/* Core Engine - Large card spanning 2 rows */}
      <div className="pp-platform-card md:row-span-2 rounded-2xl border border-violet-800/40 
        hover:border-violet-500/50 bg-slate-900 p-8 flex flex-col justify-between overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 30% 70%, rgba(139,92,246,0.1) 0%, transparent 60%)" }} />
        <div>
          <div className="w-16 h-16 rounded-xl bg-violet-900/30 border border-violet-800/50 flex items-center justify-center mb-6">
            <Icon d={PRODUCTS_DATA[0].icon} size={28} className="text-violet-400" />
          </div>
          <h3 className="text-violet-400 font-bold text-xl tracking-tight mb-2">{PRODUCTS_DATA[0].name}</h3>
          <p className="text-violet-300/70 text-sm font-semibold mb-3 tracking-wide">{PRODUCTS_DATA[0].tagline}</p>
          <p className="text-slate-400 leading-relaxed mb-6">
            {PRODUCTS_DATA[0].desc}
          </p>
          <div className="space-y-2">
            {PRODUCTS_DATA[0].features.map((f, i) => (
              <div key={i} className="pp-feature-item flex items-center gap-2 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                <span className="text-slate-300">{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-8">
          <CubeAccent />
        </div>
      </div>

      {/* AI Pipelines */}
      <div className="pp-platform-card rounded-2xl border border-cyan-800/40 hover:border-cyan-500/50 
        bg-slate-900 p-7 flex flex-col justify-between overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 80% 20%, rgba(6,182,212,0.08) 0%, transparent 60%)" }} />

        {/* Animation positioned at top right */}
        <div className="absolute top-4 right-4">
          <PipelineAccent />
        </div>

        <div>
          <div className="w-14 h-14 rounded-xl bg-cyan-900/30 border border-cyan-800/50 flex items-center justify-center mb-5">
            <Icon d={PRODUCTS_DATA[1].icon} size={24} className="text-cyan-400" />
          </div>
          <h3 className="text-cyan-400 font-bold text-lg tracking-tight mb-1.5">{PRODUCTS_DATA[1].name}</h3>
          <p className="text-cyan-300/60 text-xs font-semibold mb-3 tracking-wide">{PRODUCTS_DATA[1].tagline}</p>
          <p className="text-slate-400 text-sm leading-relaxed pr-20">
            {PRODUCTS_DATA[1].desc}
          </p>
        </div>
        <div className="h-6" /> {/* Spacer */}
      </div>

      {/* Agent Swarm */}
      <div className="pp-platform-card rounded-2xl border border-pink-800/40 hover:border-pink-500/50 
        bg-slate-900 p-7 flex flex-col justify-between overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(236,72,153,0.08) 0%, transparent 60%)" }} />

        {/* Animation positioned at top right */}
        <div className="absolute top-4 right-4">
          <SwarmAccent />
        </div>

        <div>
          <div className="w-14 h-14 rounded-xl bg-pink-900/30 border border-pink-800/50 flex items-center justify-center mb-5">
            <Icon d={PRODUCTS_DATA[2].icon} size={24} className="text-pink-400" />
          </div>
          <h3 className="text-pink-400 font-bold text-lg tracking-tight mb-1.5">{PRODUCTS_DATA[2].name}</h3>
          <p className="text-pink-300/60 text-xs font-semibold mb-3 tracking-wide">{PRODUCTS_DATA[2].tagline}</p>
          <p className="text-slate-400 text-sm leading-relaxed pr-20">
            {PRODUCTS_DATA[2].desc}
          </p>
        </div>
        <div className="h-6" /> {/* Spacer */}
      </div>

      {/* Feature List - spans 2 columns */}
      <div className="pp-platform-card md:col-span-2 rounded-2xl border border-slate-700/50 
        hover:border-slate-600/60 bg-slate-900 p-7 overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 100% 50%, rgba(139,92,246,0.05) 0%, transparent 60%)" }} />
        <h3 className="text-slate-100 font-bold text-lg mb-5 tracking-tight">Platform Capabilities</h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
          {PRODUCTS_DATA[1].features.map((f, i) => (
            <div key={i} className="pp-feature-item flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-cyan-900/20 border border-cyan-800/40 flex items-center justify-center flex-shrink-0">
                <div className="w-2 h-2 rounded-full bg-cyan-400" />
              </div>
              <span className="text-slate-300 text-sm">{f}</span>
            </div>
          ))}
          {PRODUCTS_DATA[2].features.map((f, i) => (
            <div key={i + 4} className="pp-feature-item flex items-center gap-3" style={{ animationDelay: `${(i + 4) * 0.1}s` }}>
              <div className="w-8 h-8 rounded-lg bg-pink-900/20 border border-pink-800/40 flex items-center justify-center flex-shrink-0">
                <div className="w-2 h-2 rounded-full bg-pink-400" />
              </div>
              <span className="text-slate-300 text-sm">{f}</span>
            </div>
          ))}
        </div>
      </div>

    </div>
  </section>
);




// ─── PAGE ─────────────────────────────────────────────────────────────────────
const ProductsPage = ({ setCurrentPage = () => { } }) => (
  <>
    <style>{styles}</style>
    <div className="space-y-20 max-w-6xl mx-auto px-4 pb-20">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="relative pt-16 pb-10 text-center pp-fade pp-fade-1">
        <div className="pp-dot-grid absolute inset-0 rounded-2xl -z-10 opacity-60" />

        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-600/30 
          bg-amber-900/10 mb-6">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase">Products</span>
        </div>

        <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-5">
          <span className="pp-title">Hekaos Foundry</span>
          {/* <br />
          <span className="text-slate-100">&amp; Products</span> */}
        </h1>

        <p className="text-slate-400 max-w-xl mx-auto text-base leading-relaxed">
          At HEKAOS, every product begins as an idea about how intelligence should work. Forge is where that idea is tested under heat, shaped under pressure, and hardened into something real.
        </p>
      </section>

      {/* ── TRANSFORMATION CYCLE ─────────────────────────── */}
      <TransformationCycle />

      <div className="pp-divider" />

      {/* ── PRODUCTS BENTO GRID ─────────────────────────── */}
      <ProductsSection />

      <div className="pp-divider" />


      {/* ── CTA ──────────────────────────────────────────── */}
      <PageCTA page="Products" setCurrentPage={setCurrentPage} />

    </div>
  </>
);

export default ProductsPage;